/**
 * Live Assist API client (PLAN §14). Endpoints are the ones the Go live server
 * exposes; the WebSocket contract is in packages/protocol/src/live.ts.
 */
import type { ConsentState, LiveAssistSession, LiveCapability, Page, PrivacyMode } from "@backstory/protocol";
import { API_URL, DEV_TOKEN, request } from "../../api";

const enc = encodeURIComponent;

export interface LiveSessionItem {
  id: string;
  visitId: string;
  url: string;
  title?: string;
  startedAt: string;
  liveSince: string;
  lastSeenAt: string;
  endUserId?: string;
  endUserName?: string;
  city?: string;
  region?: string;
  browser?: string;
  os?: string;
  device?: string;
  consent?: ConsentState;
  /** Capture settings this session was initialised with, straight from the SDK. */
  privacyMode?: PrivacyMode;
  recordingMode?: string;
  sdkVersion?: string;
  /** Set only while an agent console is currently connected. */
  assistId?: string;
  agentName?: string;
}

export interface VoiceToken {
  url: string;
  token: string;
  room: string;
}

interface VoiceTokenResponse {
  livekitUrl: string;
  token: string;
  room: string;
}

export const liveApi = {
  sessions: () => request<{ items: LiveSessionItem[] }>("/v1/live/sessions").then((r) => r.items ?? []),
  lookup: (code: string) => request<{ sessionId: string }>(`/v1/live/lookup?code=${enc(code.trim())}`),
  start: (sessionId: string) => request<LiveAssistSession>(`/v1/live/sessions/${enc(sessionId)}/start`, { method: "POST", body: {} }),
  requestCapability: (sessionId: string, capability: LiveCapability) =>
    request<{ requestId: string }>(`/v1/live/sessions/${enc(sessionId)}/request`, { method: "POST", body: { capability } }),
  end: (sessionId: string) => request<LiveAssistSession | undefined>(`/v1/live/sessions/${enc(sessionId)}/end`, { method: "POST", body: {} }),
  /** Drop a session from the live list (ends any assist, clears presence). */
  close: (sessionId: string) => request<{ ok: boolean; ended: boolean }>(`/v1/live/sessions/${enc(sessionId)}/close`, { method: "POST", body: {} }),
  voiceAvailable: () => request<{ available: boolean }>("/v1/live/voice"),
  voiceToken: (assistId: string) =>
    request<VoiceTokenResponse>(`/v1/live/assist/${enc(assistId)}/voice/token`, { method: "POST", body: {} })
      .then((r) => ({ url: r.livekitUrl, token: r.token, room: r.room })),
  assist: (id: string) => request<LiveAssistSession>(`/v1/live/assist/${enc(id)}`),
  history: (cursor?: string) => request<Page<LiveAssistSession>>(`/v1/live/assist${cursor ? `?cursor=${enc(cursor)}` : ""}`),
};

/** Agent socket URL for a session (same origin as the API, ws/wss scheme). */
export function agentSocketUrl(sessionId: string): string {
  const base = API_URL.replace(/^http/i, "ws");
  // Browser players consume canonical JSON. The server's default binary frame
  // is compressed ingest data and requires a decoder not shipped in the web UI.
  return `${base}/v1/live/agent?session=${enc(sessionId)}&decode=1`;
}

/**
 * Handshake subprotocols for the agent socket. Cookie sessions need none — the
 * browser sends the cookie with the upgrade — but token auth has nowhere else
 * to put the credential, because the WebSocket constructor accepts no headers.
 * Without this the socket 401s and the console sits on "connecting" forever.
 */
export function agentSocketProtocols(): string[] | undefined {
  return DEV_TOKEN ? ["backstory.bearer", DEV_TOKEN] : undefined;
}

export const liveKeys = {
  sessions: (project?: string) => ["live", "sessions", project ?? ""] as const,
  history: (cursor?: string) => ["live", "history", cursor ?? ""] as const,
  assist: (id: string) => ["live", "assist", id] as const,
};
